import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** An instant shown in the given timezone, e.g. "Sep 28, 2026, 2:30 PM". */
export const formatReadingTime = (ms: number, timeZone: string): string =>
  dayjs(ms).tz(timeZone).format('MMM D, YYYY, h:mm A');

const HOUR_MS = 60 * 60 * 1000;

/** A spacing between axis ticks, in whole local hours or days. */
export interface TickStep {
  unit: 'hour' | 'day';
  size: number;
}

const TICK_STEPS: readonly TickStep[] = [
  { unit: 'hour', size: 1 },
  { unit: 'hour', size: 2 },
  { unit: 'hour', size: 3 },
  { unit: 'hour', size: 6 },
  { unit: 'hour', size: 12 },
  { unit: 'day', size: 1 },
  { unit: 'day', size: 2 },
  { unit: 'day', size: 3 },
  { unit: 'day', size: 4 },
  { unit: 'day', size: 7 },
  { unit: 'day', size: 14 },
  { unit: 'day', size: 28 },
];

const stepMs = (step: TickStep): number =>
  step.size * (step.unit === 'hour' ? HOUR_MS : 24 * HOUR_MS);

/** The finest step that gives at most `maxTicks` ticks across the range, counting both ends. */
export const tickStepFor = (
  from: number,
  to: number,
  maxTicks: number
): TickStep =>
  TICK_STEPS.find(
    (step) => Math.floor((to - from) / stepMs(step)) + 1 <= maxTicks
  ) ?? TICK_STEPS[TICK_STEPS.length - 1];

/** Tick instants on round local wall-clock boundaries in the timezone, so they stay aligned across DST. */
export const timeTicks = (
  from: number,
  to: number,
  timeZone: string,
  step: TickStep
): number[] => {
  const ticks = new Set<number>();
  const lastDay = dayjs(to).tz(timeZone).startOf('day');
  let day = dayjs(from).tz(timeZone).startOf('day');
  for (let index = 0; !day.isAfter(lastDay); index += 1) {
    const date = day.format('YYYY-MM-DD');
    const hours =
      step.unit === 'hour'
        ? Array.from(
            { length: Math.ceil(24 / step.size) },
            (_, i) => i * step.size
          )
        : index % step.size === 0
          ? [0]
          : [];
    for (const hour of hours) {
      const at = dayjs
        .tz(`${date} ${String(hour).padStart(2, '0')}:00`, timeZone)
        .valueOf();
      if (at >= from && at <= to) ticks.add(at);
    }
    day = dayjs.tz(`${day.add(1, 'day').format('YYYY-MM-DD')} 00:00`, timeZone);
  }
  return [...ticks].sort((a, b) => a - b);
};

/** An axis label: the date at local midnight or for day steps, otherwise the hour. */
export const tickLabel = (
  ms: number,
  timeZone: string,
  step: TickStep
): string => {
  const local = dayjs(ms).tz(timeZone);
  const isMidnight = local.hour() === 0 && local.minute() === 0;
  return step.unit === 'day' || isMidnight
    ? local.format('DD MMM')
    : local.format('h A');
};

/** The tooltip's timestamp: the workspace date format with a 24-hour time, e.g. "01/03/2026 18:05". */
export const formatTooltipTime = (
  ms: number,
  timeZone: string,
  dateFormat: string
): string => dayjs(ms).tz(timeZone).format(`${dateFormat} HH:mm`);

/** A shipping event's weekday and time in the workspace timezone, e.g. "Tue, 2:05 PM", as the event card shows it. */
export const formatEventTime = (ms: number, timeZone: string): string =>
  dayjs(ms).tz(timeZone).format('ddd, h:mm A');

/** The timezone's short name at an instant, e.g. "CDT", or "GMT+2" where the zone has no abbreviation. */
export const zoneAbbreviation = (ms: number, timeZone: string): string =>
  new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(new Date(ms))
    .find((part) => part.type === 'timeZoneName')?.value ?? timeZone;

/** An instant with the timezone's short name, as the sensor report prints it, e.g. "Sep 28, 2026, 2:30 PM CDT". */
export const formatReportTime = (ms: number, timeZone: string): string =>
  `${formatReadingTime(ms, timeZone)} ${zoneAbbreviation(ms, timeZone)}`;
